// client/src/App.tsx (converted from App.js, ST-00200 — the shell now mounts the Help "?")
//
// The app shell: auth screens, the untabbed and the tabbed shells, and the chrome both share
// (ShellFrame: sidebar, mobile header, prompts, notification bell, Help launcher). ST-00283
// removed the floating support-ticket button: tickets are logged from Support in the sidebar.
import React, { type ReactNode } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { useAuth } from './types/commonComponents';
import Login from './components/auth/Login';
import Unauthorized from './components/auth/Unauthorized';
import ForgotPassword from './components/auth/ForgotPassword';
import SetPassword from './components/auth/SetPassword';
import ConnectionProblem from './components/auth/ConnectionProblem';
import EnhancedSidebar from './components/layout/EnhancedSidebar';
import AppRoutes, { type AppRoutesProps, type SharedRoute } from './components/layout/AppRoutes';
import TabHost from './components/layout/TabHost';
import TabStrip from './components/layout/TabStrip';
import PageScrollRestoration from './components/layout/PageScrollRestoration';
import { FaBars } from 'react-icons/fa';
import { DemoModeProvider } from './context/DemoModeContext';
import { useDemoMode } from './context/DemoModeContext';
import { TabProvider, TABS_ENABLED, useTabs as useTabsJs } from './context/TabContext';
import InstallPrompt from './components/common/InstallPrompt';
import PushPrompt from './components/common/PushPrompt';
import ResumeToast from './components/common/ResumeToast';
import LexiStarNotificationBell from './components/common/LexiStarNotificationBell';
import HelpLauncher from './components/common/HelpLauncher';
import AppToaster from './components/common/AppToaster';
import useLastVisitedRoute, { useLastVisitedPath } from './hooks/useLastVisitedRoute';
import useNavigationHistoryJs from './hooks/useNavigationHistory';
import useSwipeBack from './hooks/useSwipeBack';
import useNavigationKeyboard from './hooks/useNavigationKeyboard';
import useLinkInterception from './hooks/useLinkInterception';
import useTabKeyboard from './hooks/useTabKeyboard';
import NavigationBar from './components/common/NavigationBar';
import { RouteErrorBoundary } from './components/common/ErrorBoundary';
import './App.css';
import './styles/orderManagement.css';

declare global {
  interface Window {
    /** Set by index.html: fades out the pre-React splash. */
    __hideLexiBridgeLoader?: () => void;
  }
}

const AUTH_PATHS = ['/login', '/forgot-password', '/set-password', '/unauthorized'];

/** Back/forward state for the NavigationBar (useNavigationHistory, or the active tab's). */
export interface NavState {
  canGoBack: boolean;
  canGoForward: boolean;
  goBack: () => void;
  goForward: () => void;
  currentLabel: string;
}

/** The slice of TabContext (context/TabContext.js, untyped) the shell reads. */
interface TabsView {
  tabs: { id: string }[];
  activeId: string;
  nav: NavState;
  activePathname: string;
  navigateActiveTab: (url: string) => void;
  openTab: (url: string, opts?: { background?: boolean }) => string;
  closeTab: (tabId: string) => void;
  activateTab: (tabId: string) => void;
  reopenClosedTab: () => string | null;
}
const useTabs = (): TabsView => useTabsJs() as unknown as TabsView;

// hooks/useNavigationHistory.js documents `navigate` as optional in prose, which TypeScript
// reads as a required JSDoc parameter; untabbed (the BrowserRouter is window.history) it is omitted.
const useNavigationHistory = useNavigationHistoryJs as unknown as (navigate?: (delta: number) => void) => NavState;

/** Everything App hands the shells: shell chrome state plus the shared page state. */
interface ShellProps extends AppRoutesProps {
  appReady: boolean;
  isSidebarCollapsed: boolean;
  toggleSidebar: () => void;
  permissions: string[];
}

function App() {
  const { loading, authEnabled, user } = useAuth();
  const [selectedRoute, setSelectedRoute] = React.useState<SharedRoute | null>(null);
  const [selectedMonth, setSelectedMonth] = React.useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = React.useState(new Date().getFullYear());
  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(() => window.innerWidth < 768);

  // Dismiss initial splash once React app is ready
  const [appReady, setAppReady] = React.useState(false);
  React.useEffect(() => {
    if (!loading) {
      if (window.__hideLexiBridgeLoader) {
        window.__hideLexiBridgeLoader();
      }
      // Small delay so the splash fade-out completes before reveal animation starts
      const t = setTimeout(() => setAppReady(true), 100);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [loading]);

  // Auto-collapse sidebar when resizing to mobile
  React.useEffect(() => {
    const onResize = () => {
      if (window.innerWidth < 768) setIsSidebarCollapsed(true);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Get permissions from user roles or default to full access
  const roles = (user as { roles?: string[] } | null | undefined)?.roles;
  const permissions = authEnabled && user ? roles || ['*'] : ['*'];

  // While auth initializes, keep the splash visible (don't render app yet)
  if (authEnabled && loading) {
    return null;
  }

  const shellProps: ShellProps = {
    appReady,
    isSidebarCollapsed,
    toggleSidebar,
    permissions,
    selectedRoute,
    setSelectedRoute,
    selectedMonth,
    setSelectedMonth,
    selectedYear,
    setSelectedYear,
  };

  return (
    <DemoModeProvider>
      {/* ST-00259: the only ToastContainer — outside the shells, so it never remounts. */}
      <AppToaster />
      {TABS_ENABLED ? (
        <TabbedApp {...shellProps} />
      ) : (
        <Router>
          <UntabbedApp {...shellProps} />
        </Router>
      )}
    </DemoModeProvider>
  );
}

function DemoModeBanner() {
  const { isDemoMode } = useDemoMode();
  if (!isDemoMode) return null;
  return (
    <div
      style={{
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 50,
        width: 4,
        background: '#000',
        pointerEvents: 'none',
      }}
    />
  );
}

// While the server requires a password change (ST-00215, audit IAM-14) every
// API but the change itself answers 403, so the signed-in user gets only the
// auth screens and every other path leads to the change-password form.
const CHANGE_PASSWORD_PATH = '/set-password?first=1';

// ST-00303: when the session check could not reach LexiBridge, a page's address
// is not sent to sign-in: it shows "Can't reach LexiBridge" in place and opens
// as soon as the server answers.
// signedIn (tabbed shell only): an auth screen has just moved a signed-in user off
// its path. Render nothing for the moment the tabbed shell takes to take over,
// rather than sending them back to sign-in.
function AuthShell({
  mustChangePassword = false,
  connectionProblem = false,
  signedIn = false,
}: {
  mustChangePassword?: boolean;
  connectionProblem?: boolean;
  signedIn?: boolean;
}) {
  let fallback: ReactNode;
  if (connectionProblem) fallback = <ConnectionProblem />;
  else if (mustChangePassword) fallback = <Navigate to={CHANGE_PASSWORD_PATH} replace />;
  else if (signedIn) fallback = null;
  else fallback = <Navigate to="/login" replace />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route path="*" element={fallback} />
    </Routes>
  );
}

/** Tells TabbedApp each path the auth screens move the address bar to. */
function AuthPathReporter({ onPath }: { onPath: (path: string) => void }) {
  const { pathname } = useLocation();
  React.useEffect(() => {
    onPath(pathname);
  }, [pathname, onPath]);
  return null;
}

/**
 * The original single-router shell. Still the whole app when tabs are off, and
 * still what /driver gets — that screen is a focused loader-operator PWA.
 */
function UntabbedApp(props: ShellProps) {
  const { authenticated, authEnabled, mustChangePassword, connectionProblem } = useAuth();
  const location = useLocation();
  const onAuthPath = AUTH_PATHS.includes(location.pathname);
  // The loader-operator PWA (/driver) uses a frontend auth bypass (authenticated
  // stays false), so it must NOT be treated as "logged out" / redirected to login.
  const isDriverPWA = location.pathname.startsWith('/driver');
  const loggedOut = authEnabled && !authenticated && !isDriverPWA;
  const mustChange = authEnabled && mustChangePassword && !isDriverPWA;

  // Auth pages render FULL-SCREEN, without the app shell / sidebar. Also catch
  // a logged-out user on any other path and send them to the login screen.
  if (onAuthPath || loggedOut || mustChange) {
    return (
      <AuthShell
        mustChangePassword={mustChange}
        connectionProblem={loggedOut && !!connectionProblem}
      />
    );
  }

  return <UntabbedShell {...props} />;
}

/**
 * Tabbed entry point. React Router v6 refuses to nest one router inside another,
 * and every tab needs its own — so when tabs are on there is deliberately no
 * app-wide router. The shell chrome takes its navigation from TabContext instead,
 * and each tab pane owns a MemoryRouter (see TabHost).
 */
function TabbedApp(props: ShellProps) {
  const { authenticated, authEnabled, mustChangePassword, connectionProblem } = useAuth();
  // Where the app was opened. While the auth screens show, they own the address bar
  // and report each move: signing in on /login moves to '/', and the shell opens
  // there. When this was read only once, a page loaded at /login stayed on the auth
  // screens after sign-in. Their catch-all sent '/' back to /login, round and round
  // until the browser throttled it, and only a new tab got in (ST-00303). Once the
  // shell mounts, the tab host owns the address bar, and re-reading it would report
  // the active tab's path rather than where the app was opened.
  const [entryPath, setEntryPath] = React.useState(() => window.location.pathname);
  const isDriverPWA = entryPath.startsWith('/driver');
  const loggedOut = authEnabled && !authenticated && !isDriverPWA;
  const mustChange = authEnabled && mustChangePassword && !isDriverPWA;

  if (AUTH_PATHS.includes(entryPath) || loggedOut || mustChange) {
    return (
      <Router>
        <AuthShell
          mustChangePassword={mustChange}
          connectionProblem={loggedOut && !!connectionProblem}
          signedIn={!loggedOut}
        />
        <AuthPathReporter onPath={setEntryPath} />
      </Router>
    );
  }
  if (isDriverPWA) {
    return (
      <Router>
        <UntabbedShell {...props} />
      </Router>
    );
  }

  return (
    <TabProvider>
      <TabbedShell {...props} />
    </TabProvider>
  );
}

function UntabbedShell(props: ShellProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const nav: NavState = useNavigationHistory();
  const paneRef = React.useRef<HTMLDivElement>(null);
  useLastVisitedRoute();
  useSwipeBack(nav);
  useNavigationKeyboard(nav);

  return (
    <ShellFrame {...props} nav={nav} activePathname={location.pathname} onNavigate={navigate}>
      {/* The page scrolls here, not the window (ST-00087: new pages open at the top). */}
      <div ref={paneRef} className="flex-1 overflow-auto page-pane">
        <PageScrollRestoration paneRef={paneRef} />
        <RouteErrorBoundary>
          <AppRoutes {...routePropsOf(props)} />
        </RouteErrorBoundary>
      </div>
    </ShellFrame>
  );
}

/** Every tab stays mounted; the active one is the visible pane. */
function TabbedShell(props: ShellProps) {
  const tabsCtx = useTabs();
  const { nav, activePathname, navigateActiveTab, openTab } = tabsCtx;
  useLastVisitedPath(activePathname);
  useSwipeBack(nav);
  useNavigationKeyboard(nav);
  useLinkInterception(openTab);
  useTabKeyboard(tabsCtx);

  return (
    <ShellFrame
      {...props}
      nav={nav}
      activePathname={activePathname}
      onNavigate={navigateActiveTab}
      tabStrip={<TabStrip />}
    >
      <TabHost {...routePropsOf(props)} />
    </ShellFrame>
  );
}

// Page-level state that predates tabs and is still shared app-wide.
function routePropsOf({
  selectedRoute,
  setSelectedRoute,
  selectedMonth,
  setSelectedMonth,
  selectedYear,
  setSelectedYear,
}: AppRoutesProps): AppRoutesProps {
  return {
    selectedRoute,
    setSelectedRoute,
    selectedMonth,
    setSelectedMonth,
    selectedYear,
    setSelectedYear,
  };
}

interface ShellFrameProps extends ShellProps {
  nav: NavState;
  activePathname: string;
  onNavigate: (url: string) => void;
  tabStrip?: ReactNode;
  children: ReactNode;
}

/**
 * Chrome shared by both shells: sidebar, mobile header, prompts, content column.
 * It renders outside every router, so navigation arrives as props.
 */
export function ShellFrame({
  appReady,
  isSidebarCollapsed,
  toggleSidebar,
  permissions,
  nav,
  activePathname,
  onNavigate,
  tabStrip,
  children,
}: ShellFrameProps) {
  const navBarProps = { ...nav, pathname: activePathname };

  return (
    <div
      className={`flex overflow-hidden bg-gray-50 h-screen${appReady ? ' app-reveal' : ''}`}
      style={appReady ? undefined : { opacity: 0 }}
    >
      <EnhancedSidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={toggleSidebar}
        userPermissions={permissions}
        activePathname={activePathname}
      />

      <div className="mobile-nav-header">
        <button onClick={toggleSidebar} className="mobile-nav-trigger" aria-label="Open navigation">
          <FaBars size={16} />
        </button>
        <NavigationBar {...navBarProps} />
        <div className="mobile-nav-logo-wide" role="img" aria-label="LexiBridge">
          <span
            style={{
              color: 'var(--text-strong)',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 700,
              fontSize: '1.1rem',
              letterSpacing: '0.02em',
            }}
          >
            Lexi
          </span>
          <span
            style={{
              width: '2.5px',
              height: '1rem',
              background: 'var(--accent)',
              borderRadius: '2px',
              flexShrink: 0,
            }}
          />
          <span
            style={{
              color: 'var(--accent)',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 700,
              fontSize: '1.1rem',
              letterSpacing: '0.02em',
            }}
          >
            Bridge
          </span>
        </div>
      </div>

      <InstallPrompt />
      <PushPrompt onNavigate={onNavigate} />
      <ResumeToast activePathname={activePathname} onNavigate={onNavigate} />
      <LexiStarNotificationBell />
      {/* ST-00200: the round "?" in the header bar — guides for the page the user is on. */}
      <HelpLauncher activePathname={activePathname} />

      <div className="flex-1 flex flex-col overflow-hidden">
        <DemoModeBanner />
        <div className="hidden md:block">
          {tabStrip}
          <NavigationBar {...navBarProps} />
        </div>
        {children}
      </div>
    </div>
  );
}

export default App;
